/**
 * Community Operations (Global Progress)
 *
 * Client responsibilities:
 * - Emit append-only contribution events per session (idempotent doc ID)
 * - Never write global aggregates directly (server owns aggregates)
 *
 * Server responsibilities (Cloud Functions):
 * - Aggregate contributions into operations/{opId}/global + operations/{opId}/users/{uid}
 */

import { doc, runTransaction, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import type { SessionLog, PTSessionLog } from './sessionStorage';
import { getActiveOperation } from './operations';
import { isPerHandLoad } from './exerciseDatabase';

export type StageContrib = {
  movementToOrpMeters: number;
  scoutingMeters: number;
  prepTonnage: number;
  executeCount: number;
};

function isMeaningfulSession(s: SessionLog): boolean {
  if (s.sessionType === 'range') {
    const drills = (s as any).drills;
    return Array.isArray(drills) && drills.length > 0;
  }
  const exercises = (s as any).exercises;
  return Array.isArray(exercises) && exercises.length > 0;
}

export function computeStageContribFromSession(session: SessionLog): StageContrib {
  const out: StageContrib = {
    movementToOrpMeters: 0,
    scoutingMeters: 0,
    prepTonnage: 0,
    executeCount: 0,
  };

  if (!isMeaningfulSession(session)) return out;

  if (session.sessionType === 'range') {
    out.executeCount = 1;
    return out;
  }

  // PT / assessment / sprint
  const pt = session as PTSessionLog;
  const exercises = pt.exercises || [];
  exercises.forEach((ex: any) => {
    const nameLower = String(ex.exerciseName || '').toLowerCase();
    const formatLower = String(ex.format || '').toLowerCase();
    const isRuckLike = ex.exerciseType === 'ruck' || formatLower.includes('ruck') || nameLower.includes('ruck');

    // Ruck distance
    if (isRuckLike && ex.actualDistance) {
      out.movementToOrpMeters += Number(ex.actualDistance) || 0;
    }

    // Run/walk distance (unloaded cardio only, exclude ruck-like)
    if (ex.exerciseType === 'cardio' && ex.actualDistance && !ex.actualLoad && !isRuckLike) {
      out.scoutingMeters += Number(ex.actualDistance) || 0;
    }

    // Strength tonnage
    if (Array.isArray(ex.sets)) {
      ex.sets.forEach((set: any) => {
        if (set?.weight && set?.reps) {
          // Inline tonnage (per-hand/unilateral aware) — matches computeSetVolume
          // semantics. Inlined to avoid a circular import
          // (volumeTracking → sessionStorage → communityOperations).
          const reps = Number(set.reps);
          const perHandMult = set.unilateral
            ? 1
            : ((typeof set.perHand === 'boolean' ? set.perHand : isPerHandLoad(ex.exerciseName)) ? 2 : 1);
          const totalReps = set.unilateral
            ? reps + (typeof set.rightReps === 'number' ? Number(set.rightReps) : reps)
            : reps;
          out.prepTonnage += Number(set.weight) * perHandMult * totalReps;
        }
      });
    }
  });

  return out;
}

/**
 * Submit a contribution event for the active operation.
 * Idempotent: docId = `${uid}_${session.id}`. If doc exists, we do nothing.
 */
export async function submitActiveOperationContribution(session: SessionLog): Promise<void> {
  if (typeof window === 'undefined') return; // SSR safety
  if (!session?.userId || session.userId === 'anonymous') return;
  if (!session?.id) return;

  const op = getActiveOperation();
  if (!op) return;

  // Territory operations (Spearhead) use their own engine, not stage contributions
  if (op.operationType === 'territory') return;

  const stageContrib = computeStageContribFromSession(session);
  // If session contributes nothing to the op, skip writing anything.
  const sum = stageContrib.movementToOrpMeters + stageContrib.scoutingMeters + stageContrib.prepTonnage + stageContrib.executeCount;
  if (sum <= 0) return;

  const contribId = `${session.userId}_${session.id}`;
  const ref = doc(db, 'operations', op.id, 'contributions', contribId);

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return; // already submitted

    tx.set(ref, {
      opId: op.id,
      uid: session.userId,
      sessionId: session.id,
      sessionDate: session.date,
      // Use client timestamp so security rules can validate type. Server will still record create time in metadata.
      createdAt: Timestamp.now(),
      stageContrib,
      version: 1,
    });
  });
}


